import { useState, useMemo, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn, getUIRoot } from '@/ui/lib/utils';
import { ANIMATION_DURATION } from '@/ui/lib/constants';
import { useDelayedUnmount } from '@/ui/hooks/useDelayedUnmount';
import { useKeyPress } from '@/ui/hooks/useKeyPress';
import { ModalProvider } from './contexts/ModalContext';
import { ModalHeader } from './ModalHeader/ModalHeader';
import { ModalBody } from './ModalBody/ModalBody';
import { ModalContent } from './ModalContent/ModalContent';
import './modal.css';

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    children: ReactNode;
    className?: string;
}

export const Modal = ({ isOpen, onClose, children, className }: ModalProps) => {
    const [isFullscreen, setIsFullscreen] = useState(false);
    const context = useMemo(
        () => ({ isFullscreen, setIsFullscreen, onClose }),
        [isFullscreen, onClose],
    );
    const shouldRender = useDelayedUnmount(isOpen, ANIMATION_DURATION);

    useKeyPress('Escape', onClose, { enabled: isOpen });

    if (!shouldRender) return null;

    return createPortal(
        <div
            className={cn(
                'modal__overlay',
                isFullscreen && 'modal__overlay--full',
                !isOpen && 'modal__overlay--exit',
            )}
            onClick={onClose}
        >
            <ModalProvider value={context}>
                <div
                    className={cn(
                        'modal',
                        'modal--left-aligned',
                        isFullscreen && 'modal--full',
                        !isOpen && 'modal--exit',
                        className,
                    )}
                    onClick={(e) => e.stopPropagation()}
                >
                    {children}
                </div>
            </ModalProvider>
        </div>,
        getUIRoot(),
    );
};

Modal.Header = ModalHeader;
Modal.Body = ModalBody;
Modal.Content = ModalContent;
